import { MdlDialogReference, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, HostListener, Inject, InjectionToken, Optional } from '@angular/core';
import { UserPermissionsStorage } from '../../../user-permissions/user-permissions-storage';
import { EQueryStorage, EQueryCreateErrorType } from '../e-query-storage';
import { EQueryAccessionInfo } from '../../shared/e-query-accession-info';
import { EQueryCreateData } from '../models/e-query-create-data';
import { EQueryQuestionInfo } from '../e-query-details/questions/e-query-question-info';

import { ActiveEQuery } from '../models/active-e-query';

export const EQUERY_EDIT_TOKEN = new InjectionToken('EQueryEditItem');
export const ACCESSION_SUBJECT_TOKEN = new InjectionToken('AccessionSubject');
export const ACTIVE_EQUERY_TOKEN = new InjectionToken('Active eQuery info.');

interface EQueryPreviewOptions {
    hideEscalations: boolean;
    hideComments: boolean;
}

class AttachedFile {
    constructor(
        public readonly name: string,
        public readonly description: string,
    ) {}
}

@Component({
    selector: 'admin-e-query-preview-dialog',
    template: require('./admin-e-query-preview-dialog.component.html'),
    styles: [require('./admin-e-query-preview-dialog.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminEQueryPreviewDialogComponent {
    public eQueryData: EQueryCreateData;
    public accessionSubject: EQueryAccessionInfo;
    public readonly files: AttachedFile[];
    public readonly previewOptions: EQueryPreviewOptions = {
        hideComments: false,
        hideEscalations: false,
    };
    public readonly questions: EQueryQuestionInfo[];

    constructor(
        private dialog: MdlDialogReference,
        private eQueryStorage: EQueryStorage,
        @Inject(EQUERY_EDIT_TOKEN) public readonly dialogData: EQueryCreateData,
        @Inject(ACCESSION_SUBJECT_TOKEN) accessionSubject: EQueryAccessionInfo,
        @Optional() @Inject(ACTIVE_EQUERY_TOKEN) public readonly activeEQuery: ActiveEQuery,
        readonly userPermissionsStorage: UserPermissionsStorage,
        private readonly dialogService: MdlDialogService,
    ) {
        this.eQueryData = dialogData;
        this.accessionSubject = accessionSubject;
        this.files = [
            ...this.extractNewAttachments(),
            ...this.extractExistingAttachments(),
        ];
        this.questions = [
            ...this.extractNewQuestions(),
            ...this.extractExistingQuestions(),
        ];

        if (this.activeEQuery) {
            this.previewOptions = {
                hideComments: true,
                hideEscalations: true,
            };
        }
    }

    public async save() {
        const resultError = await this.eQueryStorage.createEQuery(this.eQueryData);

        if (!resultError) {
            this.dialog.hide(true);
        } else if (resultError === EQueryCreateErrorType.ActiveEQueryChanged) {
            await this.dialogService.alert('The active eQuery has been changed. Try again or refresh the edit form.').toPromise();
        }
    }

    @HostListener('document:keydown.esc')
    public async close() {
        this.dialog.hide();
    }

    private extractNewQuestions(): EQueryQuestionInfo[] {
        return this.dialogData.questions.map<EQueryQuestionInfo>(q => ({
            issueType: q.issueType,
            issueTypeDescription: q.issueDescription,
            queryText: q.queryText
        }));
    }

    private extractExistingQuestions(): EQueryQuestionInfo[] {
        if (!this.activeEQuery) {
            return [];
        }
        return this.activeEQuery.questions.map<EQueryQuestionInfo>(q => ({
            issueType: q.issueType,
            issueTypeDescription: q.issueDescription,
            queryText: q.queryText
        }));
    }

    private extractNewAttachments() {
        return this.eQueryData.files
            ? [...this.eQueryData.files].map(([file, description]) => new AttachedFile(file.name, description))
            : [];
    }

    private extractExistingAttachments() {
        return this.activeEQuery
            ? this.activeEQuery.attachments.map(a => new AttachedFile(a.uploadFileName, a.description))
            : [];
    }
}
